import { supabase } from '../lib/supabase';

export interface ImprovementAction {
  id: string;
  diagnostic_id: string;
  action_id: string;
  action_title: string;
  action_category: 'protection' | 'surveillance' | 'dissuasion';
  status: 'not_planned' | 'planned' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'high' | 'medium' | 'low';
  estimated_cost: number;
  actual_cost: number | null;
  score_impact: number;
  planned_date: string | null;
  implemented_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScoreSnapshot {
  id: string;
  diagnostic_id: string;
  score: number;
  score_protection: number;
  score_surveillance: number;
  score_dissuasion: number;
  snapshot_reason: 'initial' | 'action_completed' | 'manual_update' | 'periodic';
  action_history_id: string | null;
  notes: string | null;
  created_at: string;
}

export interface ProgressStats {
  initialScore: number;
  currentScore: number;
  scoreGain: number;
  totalPlannedActions: number;
  completedActions: number;
  inProgressActions: number;
  plannedActions: number;
  totalInvestment: number;
  estimatedInvestment: number;
  completionRate: number;
}

export async function createImprovementAction(
  diagnosticId: string,
  actionId: string,
  actionTitle: string,
  category: 'protection' | 'surveillance' | 'dissuasion',
  priority: 'high' | 'medium' | 'low',
  estimatedCost: number,
  scoreImpact: number,
  status: 'not_planned' | 'planned' | 'in_progress' | 'completed' = 'not_planned',
  plannedDate?: string,
  notes?: string
): Promise<ImprovementAction> {
  const { data, error } = await supabase
    .from('improvement_actions_history')
    .insert({
      diagnostic_id: diagnosticId,
      action_id: actionId,
      action_title: actionTitle,
      action_category: category,
      status,
      priority,
      estimated_cost: estimatedCost,
      score_impact: scoreImpact,
      planned_date: plannedDate || null,
      notes: notes || null
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating improvement action:', error);
    throw new Error('Failed to create improvement action');
  }

  return data;
}

export async function updateActionStatus(
  actionId: string,
  status: 'not_planned' | 'planned' | 'in_progress' | 'completed' | 'cancelled',
  implementedAt?: string,
  actualCost?: number,
  notes?: string
): Promise<ImprovementAction> {
  const updates: any = { status };

  if (status === 'completed') {
    updates.implemented_at = implementedAt || new Date().toISOString();
  }

  if (actualCost !== undefined) {
    updates.actual_cost = actualCost;
  }

  if (notes !== undefined) {
    updates.notes = notes;
  }

  const { data, error } = await supabase
    .from('improvement_actions_history')
    .update(updates)
    .eq('id', actionId)
    .select()
    .single();

  if (error) {
    console.error('Error updating action status:', error);
    throw new Error('Failed to update action status');
  }

  return data;
}

export async function markActionAsCompleted(
  actionId: string,
  diagnosticId: string,
  actualCost?: number,
  notes?: string
): Promise<{ action: ImprovementAction; snapshot: ScoreSnapshot }> {
  const action = await updateActionStatus(
    actionId,
    'completed',
    new Date().toISOString(),
    actualCost,
    notes
  );

  const { data: diagnostic, error: diagError } = await supabase
    .from('diagnostics')
    .select('security_score, score_protection, score_surveillance, score_dissuasion, recommendations')
    .eq('id', diagnosticId)
    .single();

  if (diagError || !diagnostic) {
    throw new Error('Failed to fetch diagnostic for snapshot');
  }

  const completedActions = await getActionsByDiagnostic(diagnosticId);
  const completedActionIds = completedActions
    .filter(a => a.status === 'completed')
    .map(a => a.action_id);

  let newScore = diagnostic.security_score || 0;
  let newScoreProtection = diagnostic.score_protection;
  let newScoreSurveillance = diagnostic.score_surveillance;
  let newScoreDissuasion = diagnostic.score_dissuasion;

  const recommendedActions = (diagnostic.recommendations || []) as any[];
  for (const actionId of completedActionIds) {
    const recommendedAction = recommendedActions.find((a: any) => a.id === actionId);
    if (recommendedAction) {
      newScore += recommendedAction.scoreImpact;

      if (recommendedAction.category === 'protection') {
        newScoreProtection += recommendedAction.scoreImpact;
      } else if (recommendedAction.category === 'surveillance') {
        newScoreSurveillance += recommendedAction.scoreImpact;
      } else if (recommendedAction.category === 'dissuasion') {
        newScoreDissuasion += recommendedAction.scoreImpact;
      }
    }
  }

  newScore = Math.min(100, Math.round(newScore));
  newScoreProtection = Math.min(100, Math.round(newScoreProtection));
  newScoreSurveillance = Math.min(100, Math.round(newScoreSurveillance));
  newScoreDissuasion = Math.min(100, Math.round(newScoreDissuasion));

  await supabase
    .from('diagnostics')
    .update({
      security_score: newScore,
      score_protection: newScoreProtection,
      score_surveillance: newScoreSurveillance,
      score_dissuasion: newScoreDissuasion
    })
    .eq('id', diagnosticId);

  const snapshot = await createScoreSnapshot(
    diagnosticId,
    newScore,
    newScoreProtection,
    newScoreSurveillance,
    newScoreDissuasion,
    'action_completed',
    actionId,
    `Action complétée: ${action.action_title}`
  );

  return { action, snapshot };
}

export async function getActionsByDiagnostic(diagnosticId: string): Promise<ImprovementAction[]> {
  const { data, error } = await supabase
    .from('improvement_actions_history')
    .select('*')
    .eq('diagnostic_id', diagnosticId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching actions:', error);
    throw new Error('Failed to fetch actions');
  }

  return data || [];
}

export async function createScoreSnapshot(
  diagnosticId: string,
  score: number,
  scoreProtection: number,
  scoreSurveillance: number,
  scoreDissuasion: number,
  reason: 'initial' | 'action_completed' | 'manual_update' | 'periodic',
  actionHistoryId?: string,
  notes?: string
): Promise<ScoreSnapshot> {
  const { data, error } = await supabase
    .from('score_snapshots')
    .insert({
      diagnostic_id: diagnosticId,
      score,
      score_protection: scoreProtection,
      score_surveillance: scoreSurveillance,
      score_dissuasion: scoreDissuasion,
      snapshot_reason: reason,
      action_history_id: actionHistoryId || null,
      notes: notes || null
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating score snapshot:', error);
    throw new Error('Failed to create score snapshot');
  }

  return data;
}

export async function getSnapshotsByDiagnostic(diagnosticId: string): Promise<ScoreSnapshot[]> {
  const { data, error } = await supabase
    .from('score_snapshots')
    .select('*')
    .eq('diagnostic_id', diagnosticId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching snapshots:', error);
    throw new Error('Failed to fetch snapshots');
  }

  return data || [];
}

export async function getProgressStats(diagnosticId: string): Promise<ProgressStats> {
  const [actions, snapshots] = await Promise.all([
    getActionsByDiagnostic(diagnosticId),
    getSnapshotsByDiagnostic(diagnosticId)
  ]);

  const completedActions = actions.filter(a => a.status === 'completed');
  const inProgressActions = actions.filter(a => a.status === 'in_progress');
  const plannedActions = actions.filter(a => a.status === 'planned');

  const initialSnapshot = snapshots.find(s => s.snapshot_reason === 'initial');
  const latestSnapshot = snapshots[snapshots.length - 1];

  const initialScore = initialSnapshot?.score || 0;
  const currentScore = latestSnapshot?.score || 0;

  const totalInvestment = completedActions.reduce((sum, a) => sum + (a.actual_cost || 0), 0);
  const estimatedInvestment = actions
    .filter(a => a.status !== 'cancelled')
    .reduce((sum, a) => sum + a.estimated_cost, 0);

  const totalPlannedActions = actions.filter(a => a.status !== 'cancelled').length;
  const completionRate = totalPlannedActions > 0
    ? (completedActions.length / totalPlannedActions) * 100
    : 0;

  return {
    initialScore,
    currentScore,
    scoreGain: currentScore - initialScore,
    totalPlannedActions,
    completedActions: completedActions.length,
    inProgressActions: inProgressActions.length,
    plannedActions: plannedActions.length,
    totalInvestment,
    estimatedInvestment,
    completionRate: Math.round(completionRate)
  };
}

export async function createInitialSnapshot(
  diagnosticId: string,
  score: number,
  scoreProtection: number,
  scoreSurveillance: number,
  scoreDissuasion: number
): Promise<ScoreSnapshot> {
  const existingSnapshots = await getSnapshotsByDiagnostic(diagnosticId);
  const hasInitialSnapshot = existingSnapshots.some(s => s.snapshot_reason === 'initial');

  if (hasInitialSnapshot) {
    return existingSnapshots.find(s => s.snapshot_reason === 'initial')!;
  }

  return createScoreSnapshot(
    diagnosticId,
    score,
    scoreProtection,
    scoreSurveillance,
    scoreDissuasion,
    'initial',
    undefined,
    'Score initial du diagnostic'
  );
}

export async function bulkCreateActionsFromRecommendations(
  diagnosticId: string,
  recommendedActions: Array<{
    id: string;
    title: string;
    category: 'protection' | 'surveillance' | 'dissuasion';
    priority: 'high' | 'medium' | 'low';
    estimatedCost: number;
    scoreImpact: number;
    description?: string;
  }>
): Promise<ImprovementAction[]> {
  const actionsToInsert = recommendedActions.map(action => ({
    diagnostic_id: diagnosticId,
    action_id: action.id,
    action_title: action.title,
    action_category: action.category,
    status: 'not_planned',
    priority: action.priority,
    estimated_cost: action.estimatedCost,
    score_impact: action.scoreImpact,
    description: action.description || ''
  }));

  const { data, error } = await supabase
    .from('improvement_actions_history')
    .insert(actionsToInsert)
    .select();

  if (error) {
    console.error('Error bulk creating actions:', error);
    throw new Error('Failed to bulk create actions');
  }

  return data || [];
}

export async function deleteAction(actionId: string): Promise<void> {
  const { error } = await supabase
    .from('improvement_actions_history')
    .delete()
    .eq('id', actionId);

  if (error) {
    console.error('Error deleting action:', error);
    throw new Error('Failed to delete action');
  }
}

export async function getCurrentScores(diagnosticId: string): Promise<{
  score: number;
  scoreProtection: number;
  scoreSurveillance: number;
  scoreDissuasion: number;
}> {
  const snapshots = await getSnapshotsByDiagnostic(diagnosticId);

  if (snapshots.length === 0) {
    const { data: diagnostic } = await supabase
      .from('diagnostics')
      .select('security_score, score_protection, score_surveillance, score_dissuasion')
      .eq('id', diagnosticId)
      .single();

    return {
      score: diagnostic?.security_score || 0,
      scoreProtection: diagnostic?.score_protection || 0,
      scoreSurveillance: diagnostic?.score_surveillance || 0,
      scoreDissuasion: diagnostic?.score_dissuasion || 0
    };
  }

  const latestSnapshot = snapshots[snapshots.length - 1];

  return {
    score: latestSnapshot.score,
    scoreProtection: latestSnapshot.score_protection,
    scoreSurveillance: latestSnapshot.score_surveillance,
    scoreDissuasion: latestSnapshot.score_dissuasion
  };
}